/**
 * 任务列表「仿真结果」列逐个 checker 的着色规则。
 *
 * 判定由后端 `EvaluationStaticVo.checkers[].verdict` 给出（与评测器统计同一宽口径），
 * 前端只做映射：通过=绿、不通过=红、其他（判不出 / 未知值）=灰。
 */
export interface CheckerTone {
  fg: string;
  bg: string;
  border: string;
  label: string;
}

const TONE_PASS: CheckerTone = { fg: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', label: '通过' };
const TONE_FAIL: CheckerTone = { fg: '#b91c1c', bg: '#fef2f2', border: '#fecaca', label: '不通过' };
const TONE_OTHER: CheckerTone = { fg: '#6b6f76', bg: '#f4f5f7', border: '#e3e5e8', label: '无判定' };

export function checkerVerdictTone(verdict: string | null | undefined): CheckerTone {
  if (verdict === 'pass') {
    return TONE_PASS;
  }
  if (verdict === 'fail') {
    return TONE_FAIL;
  }
  return TONE_OTHER;
}

/** 明细行里本函数用到的字段（与 apis/simulation.ts 的 EvaluatorItem 兼容）。 */
export interface CheckerDetailRow {
  evaluatorName?: string | null;
  type?: number | null;
  evaluationResultId?: number | null;
}

/**
 * 从 `/evaluation/evaluator/list` 的全部行里挑出某个 checker 在最新一次评测里的行。
 *
 * - 名字两侧都过 `canonical`（CamelCase / snake_case 统一），与后端 checkers 同口径；
 * - 只看 type=1 评测器 / type=3 检查项（缺省 type 视为 1），有无责事件不算；
 * - 给了 `latestEvaluationResultId` 时只保留同一次评测的行，避免重评测前的旧失败行
 *   与标签颜色对不上；老数据行没有 evaluationResultId 时不过滤。
 *
 * `canonical` 由调用方传入（组件里用 constants/evaluatorMetrics 的 canonicalEvaluatorName），
 * 让本文件不依赖路径别名、可直接 `node --test`。
 */
export function pickCheckerRows<T extends CheckerDetailRow>(
  items: readonly T[] | null | undefined,
  checkerName: string,
  latestEvaluationResultId: number | null | undefined,
  canonical: (name: string) => string,
): T[] {
  const target = canonical(checkerName);
  return (items ?? []).filter((row) => {
    const type = row.type ?? 1;
    if (type !== 1 && type !== 3) {
      return false;
    }
    if (canonical(row.evaluatorName ?? '') !== target) {
      return false;
    }
    if (latestEvaluationResultId != null && row.evaluationResultId != null) {
      return row.evaluationResultId === latestEvaluationResultId;
    }
    return true;
  });
}
